/**
 * A tiny generated reward sound for child deposits.
 *
 * Keeping this in Web Audio avoids shipping a separate binary asset and lets
 * the sound degrade gracefully on browsers/webviews that block autoplay.
 */

import { playNativeRewardSound } from '../native/rewardAudio';
import type { MoneySoundId } from '../state/childCelebrationPreferences';

const COIN_STRIKES = [
  { offset: 0, frequency: 3200 },
  { offset: 0.052, frequency: 2500 },
  { offset: 0.137, frequency: 3700 },
  { offset: 0.218, frequency: 2850 },
  { offset: 0.334, frequency: 3350 },
] as const;
const COIN_METALLIC_MODE_RATIOS = [1, 1.43, 1.91] as const;
const COIN_STRIKE_DURATION_SECONDS = 0.19;
// Keep comfortably below half volume while still being audible through a
// phone/tablet speaker. The previous 12% gain left too little headroom for
// ordinary system-volume and WebView attenuation.
const COIN_JINGLE_VOLUME = 0.13;

type SoundPreset = {
  strikes: ReadonlyArray<{ offset: number; frequency: number }>;
  ratios: ReadonlyArray<number>;
  duration: number;
  tail: number;
  volume: number;
  wave: OscillatorType;
};

const SOUND_PRESETS: Record<MoneySoundId, SoundPreset> = {
  'coin-jingle': {
    strikes: COIN_STRIKES,
    ratios: COIN_METALLIC_MODE_RATIOS,
    duration: COIN_STRIKE_DURATION_SECONDS,
    tail: 0.56,
    volume: COIN_JINGLE_VOLUME,
    wave: 'sine',
  },
  'cash-register': {
    strikes: [
      { offset: 0, frequency: 150 },
      { offset: 0.08, frequency: 1_260 },
      { offset: 0.16, frequency: 2_520 },
    ],
    ratios: [1, 2.01],
    duration: 0.24,
    tail: 0.54,
    volume: 0.12,
    wave: 'triangle',
  },
  'treasure-sparkle': {
    strikes: [
      { offset: 0, frequency: 1_180 },
      { offset: 0.055, frequency: 1_570 },
      { offset: 0.11, frequency: 2_090 },
      { offset: 0.175, frequency: 2_790 },
      { offset: 0.25, frequency: 3_520 },
    ],
    ratios: [1, 1.51],
    duration: 0.22,
    tail: 0.64,
    volume: 0.085,
    wave: 'sine',
  },
  'piggy-plink': {
    strikes: [
      { offset: 0, frequency: 720 },
      { offset: 0.14, frequency: 540 },
      { offset: 0.29, frequency: 840 },
    ],
    ratios: [1, 1.47, 2.18],
    duration: 0.16,
    tail: 0.58,
    volume: 0.11,
    wave: 'triangle',
  },
  'celebration-bells': {
    strikes: [
      { offset: 0, frequency: 523.25 },
      { offset: 0.11, frequency: 659.25 },
      { offset: 0.23, frequency: 783.99 },
    ],
    ratios: [1, 2, 3],
    duration: 0.38,
    tail: 0.78,
    volume: 0.08,
    wave: 'sine',
  },
  'drum-pop': {
    strikes: [
      { offset: 0, frequency: 118 },
      { offset: 0.095, frequency: 92 },
      { offset: 0.19, frequency: 132 },
      { offset: 0.32, frequency: 105 },
    ],
    ratios: [1, 1.52],
    duration: 0.11,
    tail: 0.5,
    volume: 0.14,
    wave: 'square',
  },
  'gentle-chime': {
    strikes: [
      { offset: 0, frequency: 880 },
      { offset: 0.22, frequency: 660 },
      { offset: 0.44, frequency: 990 },
    ],
    ratios: [1, 2.01, 3.19],
    duration: 0.48,
    tail: 1.05,
    volume: 0.055,
    wave: 'sine',
  },
};

let audioContext: AudioContext | null = null;
let lastPlayedAt = 0;
let pendingSound: MoneySoundId | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (audioContext) return audioContext;

  const browserWindow = window as typeof window & {
    webkitAudioContext?: typeof AudioContext;
  };
  const audioContextConstructor = window.AudioContext ?? browserWindow.webkitAudioContext;
  if (!audioContextConstructor) return null;

  try {
    audioContext = new audioContextConstructor();
    return audioContext;
  } catch {
    return null;
  }
}

function scheduleRewardSound(context: AudioContext, soundId: MoneySoundId): void {
  const preset = SOUND_PRESETS[soundId] ?? SOUND_PRESETS['coin-jingle'];
  const startAt = context.currentTime + 0.01;
  const master = context.createGain();
  master.gain.setValueAtTime(0.0001, startAt);
  master.gain.exponentialRampToValueAtTime(preset.volume, startAt + 0.02);
  master.gain.exponentialRampToValueAtTime(0.0001, startAt + preset.tail);
  master.connect(context.destination);

  // A coin jingle is a cluster of inharmonic impacts, not an ascending scale.
  // Five irregular strikes with three metallic modes each keep the fallback's
  // character aligned with the native iOS sound.
  for (const strike of preset.strikes) {
    const strikeStart = startAt + strike.offset;
    for (const [modeIndex, ratio] of preset.ratios.entries()) {
      const oscillator = context.createOscillator();
      const envelope = context.createGain();
      oscillator.type = modeIndex === 0 ? preset.wave : 'sine';
      oscillator.frequency.setValueAtTime(strike.frequency * ratio, strikeStart);
      envelope.gain.setValueAtTime(0.0001, strikeStart);
      envelope.gain.exponentialRampToValueAtTime(0.72 / (modeIndex + 1), strikeStart + 0.003);
      envelope.gain.exponentialRampToValueAtTime(0.0001, strikeStart + preset.duration);
      oscillator.connect(envelope);
      envelope.connect(master);
      oscillator.start(strikeStart);
      oscillator.stop(strikeStart + preset.duration + 0.015);
    }
  }
}

function resumeForJingle(
  context: AudioContext,
  onReady: () => void,
  soundToQueue: MoneySoundId | null,
): void {
  if (context.state === 'running') {
    onReady();
    return;
  }

  // WKWebView can report an interrupted context after a foreground change,
  // not only the standard `suspended` state. Treat every non-running state
  // as needing a resume; otherwise the scheduler succeeds silently but emits
  // no audible sound.
  if (soundToQueue) pendingSound = soundToQueue;
  try {
    void context
      .resume()
      .then(() => {
        if (context.state !== 'running') return;
        onReady();
      })
      .catch(() => {
        // Keep the pending sound for the next genuine child interaction.
      });
  } catch {
    // A closed or platform-blocked context must never break the reward flow.
  }
}

/** Prime/resume the context after a user gesture so a later reward can sound. */
export function primeCoinJingleAudio(): void {
  const context = getAudioContext();
  if (!context) return;
  const playPending = () => {
    const soundId = pendingSound;
    if (!soundId) return;
    pendingSound = null;
    lastPlayedAt = Date.now();
    scheduleRewardSound(context, soundId);
  };
  resumeForJingle(context, playPending, null);
}

/** Play one short, deliberately quiet coin jingle. */
export function playRewardSound(soundId: MoneySoundId): void {
  // TestFlight demonstrated that WKWebView may report a successful Web Audio
  // schedule yet stay completely silent. Prefer the iOS host's AVFoundation
  // path there; it is not tied to an asynchronous WebKit gesture allowance.
  // If native playback is temporarily unavailable, keep the established Web
  // Audio fallback so a reward never throws or blocks the rest of the UI.
  const nativePlayback = playNativeRewardSound(soundId);
  if (nativePlayback) {
    void nativePlayback
      .then((played) => {
        if (!played) playWebRewardSound(soundId);
      })
      .catch(() => playWebRewardSound(soundId));
    return;
  }
  playWebRewardSound(soundId);
}

/** Backwards-compatible preview used by existing callers and tests. */
export function playCoinJingle(): void {
  playRewardSound('coin-jingle');
}

function playWebRewardSound(soundId: MoneySoundId): void {
  const context = getAudioContext();
  if (!context) return;

  const now = Date.now();
  if (now - lastPlayedAt < 250) return;
  const play = () => {
    try {
      pendingSound = null;
      lastPlayedAt = Date.now();
      scheduleRewardSound(context, soundId);
    } catch {
      // A closed or policy-blocked context must never break the reward flow.
    }
  };

  resumeForJingle(context, play, soundId);
}
